/**
 * Generic grid and layout math for Map3Relatives (both stacked and shared timeline modes).
 *
 * This file provides the core, mode-agnostic math for grid sizing, interval calculations,
 * and y-offsets. All functions here should work for both stacked and shared timeline modes.
 *
 * Any differences between modes (such as x/y offset calculations, fallback handling, etc.)
 * should be handled by mode-specific helpers in layoutMath.Stacked.ts and layoutMath.SharedTimeline.ts,
 * which generate the parameters needed for these generic functions.
 */
// Terminology:
// - Grid: Unless otherwise specified, "grid" refers to the ReactFlow master grid. This is the single authoritative grid system for all milestone maps and their components. All ResultNodes snap to this grid.
// - MapGrid: (Not currently a separate concept in code, but could be introduced.) This would refer to a grid relative to a specific map. For example, if map 2's top-left corner is at (0, 20) on the master grid, that same point is (0,0) on its own map grid.
// - GridHeight: The height of the master ReactFlow grid (the total height needed to fit all maps stacked vertically, including spacers).
// - MapHeight: The height of a single map (number of objective rows * cell height, plus headers/subheaders).
// - Background Grid: The visu  al grid lines drawn by <Background /> in ReactFlow. These lines are styled independently but are aligned to the ReactFlow grid and our maps' columns/rows.

import { GRID_CONFIG } from '../gridConfig';
import type { MilestoneMapInterval } from '@mm-shared/types/MilestoneMap1';
import { getTotalGridWidthStacked } from './layoutMath.Stacked';
import { getTotalGridWidthSharedTimeline } from './layoutMath.SharedTimeline';
import { RelativesMapData } from '../components/Map3Relatives';

/**
 * TODO: Add a generic cell position helper here, to be implemented by mode-specific helpers.
 * Example signature:
 *   getCellPosition(intervalIndex: number, rowIndex: number, intervalType: MilestoneMapInterval, ...modeSpecificParams)
 */

/**
 * TODO: Move or reimplement getTotalGridWidthSharedTimeline here, replacing BaseInfo with intervalType as a parameter.
 * This will allow getTotalGridWidth to be fully mode-agnostic and not depend on deprecated helpers.
 */

/**
 * Returns the total width of the master ReactFlow grid, based on the map with the most intervals.
 * All maps are left-aligned to this width.
 */
export function getTotalGridWidth<T extends { intervals: Array<{ start: Date; end: Date }> }>(
  maps: T[],
  viewMode: 'stacked' | 'sharedTimeline' = 'stacked',
  intervalType?: MilestoneMapInterval,
): number {
  if (viewMode === 'stacked') {
    return getTotalGridWidthStacked(maps);
  }
  // In shared timeline mode, we need the unified timeline's total width
  if (!intervalType) {
    throw new Error('intervalType is required for shared timeline mode');
  }
  if (viewMode === 'sharedTimeline') {
    return getTotalGridWidthSharedTimeline(maps, intervalType);
  }
  throw new Error(`Invalid view mode: ${viewMode}`);
}

/**
 * Returns an array of y-offsets (in grid coordinates) for each map, indicating where each map's overlay (including its own headers) should be placed in the master grid.
 *
 * Layout math:
 * - The first map starts at y = 0 (top of the master grid). Its overlay renders its own headers at the top.
 * - Each subsequent map is stacked below the previous map, with a spacer row in between.
 * - Each overlay (Map3StaticBackground) is responsible for rendering its own headers at the top (top: 0), and status cells/objective labels below.
 *
 * This ensures:
 * - The first map's overlay aligns with the top of the master grid, and its status cells/nodes appear below its own headers.
 * - All subsequent maps are stacked with correct spacing and alignment.
 */
export function getMapYOffsets<T extends { objectives: { uuid: string }[] }>(maps: T[]): number[] {
  let yOffset = 0;
  return maps.map((m) => {
    const offset = yOffset;
    yOffset +=
      GRID_CONFIG.headerHeight +
      GRID_CONFIG.cellHeight +
      m.objectives.length * GRID_CONFIG.cellHeight +
      GRID_CONFIG.cellHeight; // spacer
    return offset;
  });
}

/**
 * Returns an array of y-offsets for RelativesMapData, using dynamic heights based on collapse states.
 * This function properly handles collapsed maps by using calculateHeightByRows instead of assuming all maps are expanded.
 *
 * @param maps - Array of RelativesMapData with embedded collapse state
 * @returns Array of Y offsets in pixels
 */
export function getMapYOffsetsWithCollapseState(relativesMaps: RelativesMapData[]): number[] {
  let yOffset = 0;
  return relativesMaps.map((relative) => {
    const offset = yOffset;
    // Use dynamic height calculation based on collapse state
    const mapHeight = calculateHeightByRows(relative) * GRID_CONFIG.cellHeight;
    yOffset += mapHeight;
    return offset;
  });
}

/**
 * Returns an array of y-offsets for RelativesMapData with special handling for fullyCollapsed maps.
 *
 * Algorithm:
 * 1. Separate fullyCollapsed from other maps
 * 2. Calculate normal flow for non-fullyCollapsed maps
 * 3. Insert fullyCollapsed section after main map with 3-row spacing
 * 4. Continue normal flow for remaining maps
 *
 * Spacing Structure:
 * Map1: [main map with normal structure]
 *       [spacer row]
 *       [fullyCollapsed lines section]  <- All fullyCollapsed maps grouped here
 *       [spacer row]
 * Map4: [next expanded map with normal structure]
 *
 * @param relativesMaps - Array of RelativesMapData with embedded collapse state
 * @param mainMapUuid - UUID of the main map (first in array, never fullyCollapsed)
 * @returns Array of Y offsets in pixels
 */
export function getMapYOffsetsWithFullyCollapsedGrouping(
  relativesMaps: RelativesMapData[],
  mainMapUuid: string,
): number[] {
  if (relativesMaps.length === 0) {
    return [];
  }

  // Separate maps by collapse state
  const mainMapIndex = relativesMaps.findIndex((map) => map.milestoneMap.uuid === mainMapUuid);
  if (mainMapIndex === -1) {
    console.error('Main map not found in relativesMaps');
    // Fallback to original algorithm
    return getMapYOffsetsWithCollapseState(relativesMaps);
  }

  const mainMap = relativesMaps[mainMapIndex];
  const fullyCollapsedMaps = relativesMaps.filter((map) => map.collapseLevel === 'fullyCollapsed');
  const nonFullyCollapsedMaps = relativesMaps.filter(
    (map) => map.collapseLevel !== 'fullyCollapsed',
  );

  // If no fullyCollapsed maps, use normal algorithm
  if (fullyCollapsedMaps.length === 0) {
    return getMapYOffsetsWithCollapseState(relativesMaps);
  }

  // STEP 1: Calculate positions for non-fullyCollapsed maps in original order
  let yOffset = 0;
  const resultOffsets: number[] = new Array(relativesMaps.length);

  // Position main map first
  const mainMapHeight = calculateHeightByRows(mainMap) * GRID_CONFIG.cellHeight;
  resultOffsets[mainMapIndex] = yOffset;
  yOffset += mainMapHeight;

  // STEP 2: Position fullyCollapsed section after main map (including its bottom spacer)
  // fullyCollapsed section uses 2 rows: 1 for the section itself + 1 spacer after
  const fullyCollapsedSectionY = yOffset; // Right after main map (including main map's bottom spacer)
  yOffset += GRID_CONFIG.cellHeight; // Space for fullyCollapsed section
  yOffset += GRID_CONFIG.cellHeight; // Spacer after fullyCollapsed section

  // Set positions for all fullyCollapsed maps to the same Y (they'll be rendered as lines)
  fullyCollapsedMaps.forEach((fullyCollapsedMap) => {
    const originalIndex = relativesMaps.findIndex(
      (map) => map.milestoneMap.uuid === fullyCollapsedMap.milestoneMap.uuid,
    );
    if (originalIndex !== -1) {
      resultOffsets[originalIndex] = fullyCollapsedSectionY;
    }
  });

  // STEP 3: Position remaining non-fullyCollapsed maps after main map
  const remainingMaps = nonFullyCollapsedMaps.filter(
    (map) => map.milestoneMap.uuid !== mainMapUuid,
  );

  remainingMaps.forEach((map) => {
    const originalIndex = relativesMaps.findIndex(
      (relativeMap) => relativeMap.milestoneMap.uuid === map.milestoneMap.uuid,
    );
    if (originalIndex !== -1) {
      const mapHeight = calculateHeightByRows(map) * GRID_CONFIG.cellHeight;
      resultOffsets[originalIndex] = yOffset;
      yOffset += mapHeight;
    }
  });

  return resultOffsets;
}

export function calculateHeightByRows(relativeMapData: RelativesMapData): number {
  const { collapseLevel, hideRowsWithoutDependencies, objectives } = relativeMapData;

  // Special case: fullyCollapsed maps are removed from the normal flow
  if (collapseLevel === 'fullyCollapsed') {
    return 0; // Completely removed from Y-offset calculations
  }

  // Default "expanded" settings.
  let hasTitleBar = true;
  let hasColumnHeadings = true;
  let showObjectives = true;
  let hasBottomSpacer = true;
  let hasTopSpacer = false;

  let rowCount = 0;
  switch (collapseLevel) {
    case 'titleOnly':
      hasColumnHeadings = false;
      showObjectives = false;
      break;
    case 'expanded':
      hasTopSpacer = false;
      hasTitleBar = true;
      hasColumnHeadings = true;
      showObjectives = true;
      hasBottomSpacer = true;
      break;
    default:
      console.error(`Invalid collapse level: ${collapseLevel}`);
  }

  // Now count up all the rows we need for the current display state.
  if (hasTitleBar) {
    // One row high
    rowCount++;
  }
  if (hasColumnHeadings) {
    // Two rows high
    rowCount++;
    rowCount++;
  }
  if (showObjectives) {
    // One row per objective
    rowCount += objectives.length;
  }
  if (hasBottomSpacer) {
    // As of 6/3/2025, we always have a bottom spacer.
    rowCount++;
  }
  if (hasTopSpacer) {
    // As of 6/3/2025, no map configurations have a top spacer.
    rowCount++;
  }

  return rowCount;
}

/**
 * Returns the total height of the master ReactFlow grid, including all maps and spacers.
 * This is the height needed for the <ReactFlow> container.
 * Uses the same positioning algorithm as getMapYOffsetsWithFullyCollapsedGrouping to ensure consistency.
 */
export function getTotalGridHeight(maps: RelativesMapData[]): number {
  if (maps.length === 0) return 0;

  // Find main map (first in array)
  const mainMap = maps[0];
  if (!mainMap) return 0;

  // Use the same algorithm as getMapYOffsetsWithFullyCollapsedGrouping for consistency
  const yOffsets = getMapYOffsetsWithFullyCollapsedGrouping(maps, mainMap.milestoneMap.uuid);

  // Find the actual end of the grid by checking both fullyCollapsed and non-fullyCollapsed maps
  const fullyCollapsedMaps = maps.filter((map) => map.collapseLevel === 'fullyCollapsed');
  const nonFullyCollapsedMaps = maps.filter((map) => map.collapseLevel !== 'fullyCollapsed');

  let totalHeight = 0;

  // If there are fullyCollapsed maps, the grid ends after the fullyCollapsed section + spacer
  if (fullyCollapsedMaps.length > 0) {
    // Find where the fullyCollapsed section ends (they all have the same Y offset)
    const fullyCollapsedY =
      yOffsets[maps.findIndex((map) => map.collapseLevel === 'fullyCollapsed')];
    totalHeight = Math.max(totalHeight, fullyCollapsedY + 2 * GRID_CONFIG.cellHeight); // fullyCollapsed + spacer
  }

  // Check if any non-fullyCollapsed maps extend beyond the fullyCollapsed section
  nonFullyCollapsedMaps.forEach((map, idx) => {
    const originalIndex = maps.findIndex((m) => m.milestoneMap.uuid === map.milestoneMap.uuid);
    const mapYOffset = yOffsets[originalIndex];
    const mapHeight = calculateHeightByRows(map) * GRID_CONFIG.cellHeight;
    totalHeight = Math.max(totalHeight, mapYOffset + mapHeight);
  });

  return totalHeight;
}

export type ColumnInfo = {
  width: number;
  startDate: Date;
  endDate: Date;
  masterColumnIndex: number;
  statusColumnIndex: number | null;
  columnIntervalUnit: MilestoneMapInterval;
  globalX: number;
  localX: number | null;
};

/**
 * Returns the x-coordinate of the column in the global (master) grid.
 *
 * GLOBAL X: This is the absolute X position of the column in the master timeline grid, measured from the left edge of the entire grid (including the objectives column).
 * Use this for positioning elements that should align with the global timeline, regardless of map-specific offsets.
 *
 * @param columnInfo
 * @returns number in pixels
 */
export function getGlobalXForColumn(columnInfo: ColumnInfo): number {
  return columnInfo.globalX;
}

/**
 * Returns the x-coordinate of the column in the map's local grid (relative to the map's left edge).
 *
 * LOCAL X: This is the X position of the column relative to the map's own overlay or container. Use this when the parent container is already offset (e.g., in shared timeline mode, when the map overlay is shifted horizontally).
 *
 * @param columnInfo
 * @returns number in pixels
 */
export function getLocalXForColumn(columnInfo: ColumnInfo): number {
  return columnInfo.localX ?? 0;
}

export function isStatusColumn(columnInfo: ColumnInfo): boolean {
  return columnInfo.statusColumnIndex !== null;
}
